import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { DollarSign, ShoppingCart, Package, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await axios.get(`${API}/admin/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Revenue',
      value: stats ? `$${stats.total_revenue.toFixed(2)}` : '$0.00',
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      title: 'Total Orders',
      value: stats ? stats.total_orders : '0',
      icon: ShoppingCart,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Total Products',
      value: stats ? stats.total_products : '0',
      icon: Package,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      title: 'Low Stock Alert',
      value: stats ? stats.low_stock_count : '0',
      icon: AlertTriangle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    },
  ];

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's an overview of your store.</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-card border border-border hover:border-accent/50 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-full ${card.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className="text-3xl font-bold text-foreground">{card.value}</p>
                </div>
              );
            })}
          </div>
        )}

        {stats && stats.low_stock_count > 0 && (
          <div className="mt-8 p-6 rounded-2xl bg-orange-500/10 border border-orange-500/30">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Low Stock Warning</h3>
                <p className="text-muted-foreground">
                  You have {stats.low_stock_count} product(s) with low stock (less than 20 units). 
                  Please restock soon to avoid running out.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};